/**
 * Text Processing Utilities
 * Functions for slug generation, HTML stripping, and text manipulation
 */

/**
 * Generate SEO-friendly slug from title
 * - Removes HTML tags
 * - Normalizes accented characters
 * - Removes special characters
 * - Replaces spaces with hyphens
 * - Appends ID for uniqueness
 *
 * @param title - The article/post title
 * @param id - Unique identifier to append to slug
 * @returns SEO-friendly slug with ID
 *
 * @example
 * ```typescript
 * generateSeoSlug("Real Madrid Wins!", 123)
 * // Returns: "real-madrid-wins-123"
 *
 * generateSeoSlug("Mbappé's Goal", 456)
 * // Returns: "mbappes-goal-456"
 * ```
 */
export function generateSeoSlug(title: string, id: number): string {
  const idString = String(id)
  const maxTitleLength = 60 - idString.length - 1 // Reserve space for "-{id}"

  const cleanTitle = title
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .toLowerCase()
    .normalize('NFD') // Normalize accented characters
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
    .substring(0, maxTitleLength > 0 ? maxTitleLength : 60) // Limit length for SEO

  return cleanTitle ? `${cleanTitle}-${id}` : `${id}`
}

/**
 * Extract ID from SEO slug
 * Parses slugs in format "title-text-123" to extract the ID
 *
 * @param slug - The SEO slug
 * @returns Extracted ID as number, or null if not found
 *
 * @example
 * ```typescript
 * extractIdFromSlug("real-madrid-wins-123")
 * // Returns: 123
 *
 * extractIdFromSlug("456") // Just ID
 * // Returns: null
 * ```
 */
export function extractIdFromSlug(slug: string): number | null {
  const idMatch = slug.match(/-(\d+)$/)
  return idMatch ? parseInt(idMatch[1], 10) : null
}

/**
 * Strip HTML tags and decode entities
 * Comprehensive HTML entity decoder with fallback patterns
 *
 * @param html - HTML string to clean
 * @returns Plain text with decoded entities
 *
 * @example
 * ```typescript
 * stripHtml("<p>Hello &amp; welcome!</p>")
 * // Returns: "Hello & welcome!"
 *
 * stripHtml("Price: &euro;10&nbsp;only")
 * // Returns: "Price: €10 only"
 * ```
 */
export function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&rsquo;/g, "'") // Right single quotation mark
    .replace(/&lsquo;/g, "'") // Left single quotation mark
    .replace(/&rdquo;/g, '"') // Right double quotation mark
    .replace(/&ldquo;/g, '"') // Left double quotation mark
    .replace(/&amp;/g, '&') // Ampersand
    .replace(/&quot;/g, '"') // Quotation mark
    .replace(/&apos;/g, "'") // Apostrophe
    .replace(/&nbsp;/g, ' ') // Non-breaking space
    .replace(/&lt;/g, '<') // Less than
    .replace(/&gt;/g, '>') // Greater than
    .replace(/&hellip;/g, '...') // Horizontal ellipsis
    .replace(/&#8217;/g, "'") // Right single quotation mark (numeric)
    .replace(/&#8216;/g, "'") // Left single quotation mark (numeric)
    .replace(/&#8220;/g, '"') // Left double quotation mark (numeric)
    .replace(/&#8221;/g, '"') // Right double quotation mark (numeric)
    .replace(/&#8230;/g, '...') // Horizontal ellipsis (numeric)
    .replace(/&#039;/g, "'") // Apostrophe (numeric)
    .replace(/&euro;/g, '€') // Euro sign
    .replace(/&pound;/g, '£') // Pound sign
    .replace(/&yen;/g, '¥') // Yen sign
    .replace(/&copy;/g, '©') // Copyright
    .replace(/&reg;/g, '®') // Registered trademark
    .replace(/&trade;/g, '™') // Trademark
    .trim()
}

/**
 * Truncate text to specified length with ellipsis
 * Smart truncation that doesn't break words
 *
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @param suffix - Suffix to append (default: "...")
 * @returns Truncated text
 *
 * @example
 * ```typescript
 * truncateText("This is a long article", 10)
 * // Returns: "This is a..."
 *
 * truncateText("Short", 50)
 * // Returns: "Short"
 * ```
 */
export function truncateText(
  text: string,
  maxLength: number,
  suffix: string = '...'
): string {
  if (text.length <= maxLength) {
    return text
  }

  // Find the last space before maxLength
  const truncated = text.substring(0, maxLength)
  const lastSpace = truncated.lastIndexOf(' ')

  if (lastSpace > 0) {
    return truncated.substring(0, lastSpace) + suffix
  }

  return truncated + suffix
}

/**
 * Extract plain text from HTML with proper spacing
 * Better than stripHtml for readable text extraction
 *
 * @param html - HTML content
 * @returns Plain text with proper spacing
 *
 * @example
 * ```typescript
 * extractTextFromHtml("<p>Hello</p><p>World</p>")
 * // Returns: "Hello World"
 * ```
 */
export function extractTextFromHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, ' ') // Replace <br> with space
    .replace(/<\/p>/gi, ' ') // Add space after paragraphs
    .replace(/<[^>]*>/g, '') // Remove all HTML tags
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim()
}

/**
 * Clean and normalize whitespace in text
 *
 * @param text - Text with messy whitespace
 * @returns Text with normalized whitespace
 *
 * @example
 * ```typescript
 * normalizeWhitespace("Hello   world\n\n\n  test")
 * // Returns: "Hello world test"
 * ```
 */
export function normalizeWhitespace(text: string): string {
  return text
    .replace(/\s+/g, ' ') // Replace multiple whitespace with single space
    .replace(/\n+/g, ' ') // Replace newlines with space
    .trim()
}

/**
 * Convert text to title case
 *
 * @param text - Text to convert
 * @returns Title-cased text
 *
 * @example
 * ```typescript
 * toTitleCase("hello world")
 * // Returns: "Hello World"
 * ```
 */
export function toTitleCase(text: string): string {
  return text
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Excerpt generation with HTML stripping
 * Generates a clean excerpt from HTML content
 *
 * @param html - HTML content
 * @param maxLength - Maximum excerpt length
 * @param suffix - Suffix to append (default: "...")
 * @returns Clean excerpt
 *
 * @example
 * ```typescript
 * generateExcerpt("<p>Long article...</p>", 100)
 * // Returns: "Long article..."
 * ```
 */
export function generateExcerpt(
  html: string,
  maxLength: number = 160,
  suffix: string = '...'
): string {
  const text = extractTextFromHtml(html)
  return truncateText(text, maxLength, suffix)
}
